import React from 'react';
import {
  Box, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SectionCard from '../SectionCard';
import InvButton from '../InvButton';
import { InvTextField } from '../InvField';
import InvItemAutocomplete from '../InvItemAutocomplete';
import { formatCurrency, lineTotal } from '../../utils';
import { IPD_COLORS } from '../../theme';

const TABLE_CELL_SX = { py: 1.25, verticalAlign: 'middle' };

const NUM_CELL = {
  align: 'right',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

const MONEY_CELL = {
  ...NUM_CELL,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 13,
};

export default function PoLineItemsEditor({
  lines,
  onAddLine,
  onRemoveLine,
  onUpdateLine,
}) {
  const validLineCount = lines.filter(l => l.item_id && l.ordered_quantity && l.unit_rate).length;

  return (
    <SectionCard
      title="Line Items"
      actions={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
            {validLineCount} item{validLineCount !== 1 ? 's' : ''}
          </Typography>
          <InvButton size="small" startIcon={<AddIcon />} onClick={() => onAddLine()}>
            Add Row
          </InvButton>
        </Box>
      }
      sx={{ height: '100%' }}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 40, px: 0.5 }} />
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 40, px: 1 }}>#</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: '34%' }}>Item</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, ...NUM_CELL, width: '10%' }}>Qty</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, ...MONEY_CELL, width: '14%' }}>Rate (₹)</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, ...NUM_CELL, width: '9%' }}>Tax %</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, ...NUM_CELL, width: '9%' }}>Disc %</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, ...MONEY_CELL, width: '14%' }}>Total</TableCell>
              <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 40, px: 0.5, textAlign: 'center' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary, py: 4 }}>
                  No line items — click &quot;Add Row&quot; to begin
                </TableCell>
              </TableRow>
            ) : lines.map((line, i) => {
              const rowTotal = lineTotal(
                line.ordered_quantity,
                line.unit_rate,
                line.tax_percent,
                line.discount_percent,
              );
              const rowIncomplete = line.item_id && (!line.ordered_quantity || !line.unit_rate);

              return (
                <TableRow
                  key={i}
                  sx={rowIncomplete ? { backgroundColor: IPD_COLORS.warning.bg } : undefined}
                >
                  <TableCell sx={{ ...TABLE_CELL_SX, px: 0.5, textAlign: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => onRemoveLine(i)}
                      disabled={lines.length <= 1}
                      sx={{ color: IPD_COLORS.error.text }}
                      aria-label={`Remove line ${i + 1}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, color: IPD_COLORS.textSecondary, fontSize: 12, px: 1 }}>
                    {i + 1}
                  </TableCell>
                  <TableCell sx={TABLE_CELL_SX}>
                    <InvItemAutocomplete
                      value={line.item_id}
                      onChange={(itemId, item) => onUpdateLine(i, 'item_id', itemId, item)}
                    />
                  </TableCell>
                  <TableCell sx={TABLE_CELL_SX}>
                    <InvTextField
                      value={line.ordered_quantity}
                      label=""
                      onChange={e => onUpdateLine(i, 'ordered_quantity', e.target.value)}
                      type="number"
                      inputProps={{ min: 0.01, step: 0.01 }}
                    />
                  </TableCell>
                  <TableCell sx={TABLE_CELL_SX}>
                    <InvTextField
                      value={line.unit_rate}
                      label=""
                      onChange={e => onUpdateLine(i, 'unit_rate', e.target.value)}
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </TableCell>
                  <TableCell sx={TABLE_CELL_SX}>
                    <InvTextField
                      value={line.tax_percent}
                      label=""
                      placeholder="0"
                      onChange={e => onUpdateLine(i, 'tax_percent', e.target.value)}
                      type="number"
                      inputProps={{ min: 0, max: 28, step: 0.01 }}
                    />
                  </TableCell>
                  <TableCell sx={TABLE_CELL_SX}>
                    <InvTextField
                      value={line.discount_percent}
                      label=""
                      placeholder="0"
                      onChange={e => onUpdateLine(i, 'discount_percent', e.target.value)}
                      type="number"
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...MONEY_CELL, fontWeight: 600 }}>
                    {formatCurrency(rowTotal)}
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, px: 0.5, textAlign: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => onAddLine(i)}
                      sx={{ color: IPD_COLORS.primary }}
                      aria-label={`Add row below line ${i + 1}`}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </SectionCard>
  );
}
